import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PURGE } from 'redux-persist';
import type { RootState } from 'app/store';
import type { IDatabase, ISchema, ISchemaLevelObject, ITest } from 'utils/types';

type IFlow = 'not-selected' | 'snowflake' | 'ddl' | 'example-ddl';

type IEnvironmentManagement = 'not-selected' | 'managed' | 'not-managed';

interface SchemaStore {
  [key: string]: ISchema[];
}

interface SchemaLevelObjectStore {
  [key: string]: ISchemaLevelObject[];
}

export interface IBuilderState {
  flow: IFlow;
  dataProductName: string;
  dataProductDescription: string;
  dataProductOwner: string;
  dataProductVersion: string;
  dataQualityTests: string;
  schemas: SchemaStore;
  schemaLevelObjects: SchemaLevelObjectStore;
  selectedDatabases: IDatabase[];
  selectedSchemas: ISchema[];
  selectedSchemaLevelObjects: ISchemaLevelObject[];
  environmentManagement: IEnvironmentManagement;
  generateSoleFromObjectsOutput: string;
  soleOutput: string;
  numberOfSelectedSourceTables: number;
  numberOfGeneratedTests: number;
  generatedAutomatedTests: boolean;
  generatedTests: ITest[];
}

export const initialState: IBuilderState = {
  flow: 'not-selected',
  dataProductName: '',
  dataProductDescription: '',
  dataProductOwner: '',
  dataProductVersion: '0.1.0',
  dataQualityTests: '',
  schemas: {},
  schemaLevelObjects: {},
  selectedDatabases: [],
  selectedSchemas: [],
  selectedSchemaLevelObjects: [],
  environmentManagement: 'not-selected',
  generateSoleFromObjectsOutput: '',
  soleOutput: '',
  numberOfSelectedSourceTables: 0,
  numberOfGeneratedTests: 0,
  generatedAutomatedTests: false,
  generatedTests: [],
};

export const builderSlice = createSlice({
  name: 'builder',
  initialState,
  reducers: {
    setFlow: (state, action: PayloadAction<IFlow>) => {
      state.flow = action.payload;
    },
    setDataProductName: (state, action: PayloadAction<string>) => {
      state.dataProductName = action.payload;
    },
    setDataProductDescription: (state, action: PayloadAction<string>) => {
      state.dataProductDescription = action.payload;
    },
    setDataProductOwner: (state, action: PayloadAction<string>) => {
      state.dataProductOwner = action.payload;
    },
    setDataProductVersion: (state, action: PayloadAction<string>) => {
      state.dataProductVersion = action.payload;
    },
    setDataQualityTests: (state, action: PayloadAction<string>) => {
      state.dataQualityTests = action.payload;
    },
    addSchemas: (state, action: PayloadAction<{ databaseKey: string; schemas: ISchema[] }>) => {
      state.schemas[action.payload.databaseKey] = action.payload.schemas;
    },
    addSchemaLevelObjects: (
      state,
      action: PayloadAction<{ schemaKey: string; schemaLevelObjects: ISchemaLevelObject[] }>,
    ) => {
      state.schemaLevelObjects[action.payload.schemaKey] = action.payload.schemaLevelObjects;
    },
    selectDatabase: (state, action: PayloadAction<IDatabase>) => {
      const { database } = action.payload;

      // Add database to selection if not already selected
      const isDatabaseSelected = state.selectedDatabases.some((db) => db.database === database);

      if (!isDatabaseSelected) {
        state.selectedDatabases.push(action.payload);
      }

      // Remove any schemas belonging to this database from selection
      // This maintains the hierarchy where selecting a database implies
      // all its schemas are included
      if (database in state.schemas) {
        state.selectedSchemas = state.selectedSchemas.filter((schema) => schema.database !== database);
      }
    },
    unselectDatabase: (state, action: PayloadAction<IDatabase>) => {
      const { database } = action.payload;

      // Remove database from selection
      const databaseIndex = state.selectedDatabases.findIndex((db) => db.database === database);

      if (databaseIndex !== -1) {
        state.selectedDatabases.splice(databaseIndex, 1);
      }

      // Remove any schemas belonging to this database from selection
      state.selectedSchemas = state.selectedSchemas.filter((schema) => schema.database !== database);
    },
    selectSchema: (state, action: PayloadAction<ISchema>) => {
      const { database, schema } = action.payload;

      // Add schema to selection
      state.selectedSchemas.push(action.payload);

      // Remove any schema-level objects belonging to this schema from selection
      // This maintains the hierarchy where selecting a schema implies all its objects are included
      const schemaKey = `${database}.${schema}`;
      if (schemaKey in state.schemaLevelObjects) {
        state.selectedSchemaLevelObjects = state.selectedSchemaLevelObjects.filter(
          (object) => object.schema !== schema || object.database !== database,
        );
      }

      // Check if all schemas within the database are now selected
      const allSchemasSelected = state.schemas[database]?.every((schemaItem) => {
        return state.selectedSchemas.some(
          (selectedSchema) =>
            selectedSchema.database === schemaItem.database && selectedSchema.schema === schemaItem.schema,
        );
      });

      // If all schemas are selected, promote selection to database level
      // and remove individual schema selections
      if (allSchemasSelected) {
        // Add database to selection if not already selected
        const isDatabaseSelected = state.selectedDatabases.some((db) => db.database === database);

        if (!isDatabaseSelected) {
          state.selectedDatabases.push({ database });
        }

        // Remove all schemas of this database from selection
        state.selectedSchemas = state.selectedSchemas.filter((selectedSchema) => selectedSchema.database !== database);
      }
    },
    unselectSchema: (state, action: PayloadAction<ISchema>) => {
      const { database, schema } = action.payload;

      // If database is selected, unselect it and select all other schemas in that database
      const databaseIndex = state.selectedDatabases.findIndex((db) => db.database === database);

      if (databaseIndex !== -1) {
        // Remove database from selection
        state.selectedDatabases.splice(databaseIndex, 1);

        // Add all schemas from this database to selection except the one being unselected
        if (database in state.schemas) {
          state.schemas[database].forEach((schemaItem) => {
            if (schemaItem.schema !== schema) {
              state.selectedSchemas.push({
                database,
                schema: schemaItem.schema,
              });
            }
          });
        }
      } else {
        // Remove schema from selection if it exists
        const schemaIndex = state.selectedSchemas.findIndex((s) => s.schema === schema && s.database === database);

        if (schemaIndex !== -1) {
          state.selectedSchemas.splice(schemaIndex, 1);
        }
      }

      // Remove any schema-level objects belonging to this schema from selection
      const schemaKey = `${database}.${schema}`;
      if (schemaKey in state.schemaLevelObjects) {
        state.selectedSchemaLevelObjects = state.selectedSchemaLevelObjects.filter(
          (object) => object.schema !== schema || object.database !== database,
        );
      }
    },
    selectSchemaLevelObject: (state, action: PayloadAction<ISchemaLevelObject>) => {
      const { database, schema } = action.payload;

      // Add schema-level object to selection
      state.selectedSchemaLevelObjects.push(action.payload);

      // Check if all schema-level objects within the schema are now selected
      const schemaKey = `${String(database)}.${String(schema)}`;
      const allObjectsSelected = state.schemaLevelObjects[schemaKey]?.every((object) => {
        return state.selectedSchemaLevelObjects.some(
          (selectedObject) =>
            selectedObject.database === object.database &&
            selectedObject.schema === object.schema &&
            selectedObject.name === object.name &&
            selectedObject.type === object.type,
        );
      });

      // If all schema-level objects are selected, promote selection to schema level
      // and remove individual object selections
      if (allObjectsSelected) {
        // Add schema to selection if not already selected
        const isSchemaSelected = state.selectedSchemas.some((s) => s.database === database && s.schema === schema);

        if (!isSchemaSelected) {
          state.selectedSchemas.push({ database, schema });
        }

        // Remove all objects of this schema from selection
        state.selectedSchemaLevelObjects = state.selectedSchemaLevelObjects.filter(
          (object) => object.schema !== schema || object.database !== database,
        );

        // Check if all schemas within the database are now selected
        const allSchemasSelected = state.schemas[database]?.every((schemaItem) => {
          return state.selectedSchemas.some(
            (selectedSchema) =>
              selectedSchema.database === schemaItem.database && selectedSchema.schema === schemaItem.schema,
          );
        });

        // If all schemas are selected, promote selection to database level
        // and remove individual schema selections
        if (allSchemasSelected) {
          // Add database to selection if not already selected
          const isDatabaseSelected = state.selectedDatabases.some((db) => db.database === database);

          if (!isDatabaseSelected) {
            state.selectedDatabases.push({ database });
          }

          // Remove all schemas of this database from selection
          state.selectedSchemas = state.selectedSchemas.filter(
            (selectedSchema) => selectedSchema.database !== database,
          );
        }
      }
    },
    unselectSchemaLevelObject: (state, action: PayloadAction<ISchemaLevelObject>) => {
      const { database, schema, name, type } = action.payload;

      // If database is selected, handle database-level unselection
      const databaseIndex = state.selectedDatabases.findIndex((db) => db.database === database);

      if (databaseIndex !== -1) {
        // Remove database from selection
        state.selectedDatabases.splice(databaseIndex, 1);

        // Add all schemas from this database to selection
        if (database in state.schemas) {
          state.schemas[database].forEach((schemaItem) => {
            state.selectedSchemas.push({
              database,
              schema: schemaItem.schema,
            });
          });
        }
      }

      // If schema is selected, handle schema-level unselection
      const schemaIndex = state.selectedSchemas.findIndex((s) => s.database === database && s.schema === schema);

      if (schemaIndex !== -1) {
        // Remove schema from selection
        state.selectedSchemas.splice(schemaIndex, 1);

        // Add all objects from this schema to selection except the one being unselected
        const schemaKey = `${String(database)}.${String(schema)}`;
        if (schemaKey in state.schemaLevelObjects) {
          state.schemaLevelObjects[schemaKey].forEach((object) => {
            if (object.name !== name || object.type !== type) {
              state.selectedSchemaLevelObjects.push({
                database: object.database,
                schema: object.schema,
                name: object.name,
                type: object.type,
              });
            }
          });
        }
      } else {
        // Remove the specific schema-level object from selection if it exists
        const objectIndex = state.selectedSchemaLevelObjects.findIndex(
          (object) =>
            object.database === database && object.schema === schema && object.name === name && object.type === type,
        );

        if (objectIndex !== -1) {
          state.selectedSchemaLevelObjects.splice(objectIndex, 1);
        }
      }
    },
    clearSelectedObjects: (state) => {
      // Reset all selection state and schema information
      state.selectedDatabases = [];
      state.selectedSchemas = [];
      state.selectedSchemaLevelObjects = [];
      state.schemas = {};
      state.schemaLevelObjects = {};
    },
    clearSelectedObjectsForDatabase: (state, action: PayloadAction<string>) => {
      const database = action.payload;

      // Early return if database doesn't exist in schemas
      if (state.schemas[database] === undefined) {
        return;
      }

      // Remove all schemas of this database from selection
      state.selectedSchemas = state.selectedSchemas.filter((schema) => schema.database !== database);
    },
    setEnvironmentManagement: (state, action: PayloadAction<IEnvironmentManagement>) => {
      state.environmentManagement = action.payload;
    },
    setGenerateSoleFromObjectsOutput: (state, action: PayloadAction<string>) => {
      state.generateSoleFromObjectsOutput = action.payload;
    },
    setSoleOutput: (state, action: PayloadAction<string>) => {
      state.soleOutput = action.payload;
    },
    setNumberOfSelectedSourceTables: (state, action: PayloadAction<number>) => {
      state.numberOfSelectedSourceTables = action.payload;
    },
    setNumberOfGeneratedTests: (state, action: PayloadAction<number>) => {
      state.numberOfGeneratedTests = action.payload;
    },
    setGenerateAutomatedTests: (state, action: PayloadAction<boolean>) => {
      state.generatedAutomatedTests = action.payload;
    },
    setGeneratedTests: (state, action: PayloadAction<ITest[]>) => {
      state.generatedTests = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(PURGE, () => {
      return initialState;
    });
  },
});

export const {
  setFlow,
  setDataProductName,
  setDataProductDescription,
  setDataProductOwner,
  setDataProductVersion,
  setDataQualityTests,
  addSchemas,
  addSchemaLevelObjects,
  selectDatabase,
  unselectDatabase,
  selectSchema,
  unselectSchema,
  selectSchemaLevelObject,
  unselectSchemaLevelObject,
  clearSelectedObjects,
  clearSelectedObjectsForDatabase,
  setEnvironmentManagement,
  setGenerateSoleFromObjectsOutput,
  setSoleOutput,
  setNumberOfSelectedSourceTables,
  setNumberOfGeneratedTests,
  setGenerateAutomatedTests,
  setGeneratedTests,
} = builderSlice.actions;

export const selectFlow = (state: RootState): IFlow => state.builder.flow;
export const selectDataProductName = (state: RootState): string => state.builder.dataProductName;
export const selectDataProductDescription = (state: RootState): string => state.builder.dataProductDescription;
export const selectDataProductOwner = (state: RootState): string => state.builder.dataProductOwner;
export const selectDataProductVersion = (state: RootState): string => state.builder.dataProductVersion;
export const selectDataQualityTests = (state: RootState): string => state.builder.dataQualityTests;
export const selectSelectedDatabases = (state: RootState): IDatabase[] => state.builder.selectedDatabases;
export const selectSelectedSchemas = (state: RootState): ISchema[] => state.builder.selectedSchemas;
export const selectSelectedSchemaLevelObjects = (state: RootState): ISchemaLevelObject[] =>
  state.builder.selectedSchemaLevelObjects;
export const selectSchemas = (state: RootState): SchemaStore => state.builder.schemas;
export const selectSchemaLevelObjects = (state: RootState): SchemaLevelObjectStore => state.builder.schemaLevelObjects;
export const selectEnvironmentManagement = (state: RootState): IEnvironmentManagement =>
  state.builder.environmentManagement;
export const selectGenerateSoleFromObjectsOutput = (state: RootState): string =>
  state.builder.generateSoleFromObjectsOutput;
export const selectSoleOutput = (state: RootState): string => state.builder.soleOutput;
export const selectNumberOfSelectedSourceTables = (state: RootState): number =>
  state.builder.numberOfSelectedSourceTables;
export const selectNumberOfGeneratedTests = (state: RootState): number => state.builder.numberOfGeneratedTests;
export const selectGenerateAutomatedTests = (state: RootState): boolean => state.builder.generatedAutomatedTests;
export const selectGeneratedTests = (state: RootState): ITest[] => state.builder.generatedTests;

export default builderSlice.reducer;
